import React, { useCallback, useMemo, useState } from "react";
import { DatePicker, DatePickerInput } from "@carbon/react";

/**
 * Componente de filtro de fechas:
 *  - "date_range": selector de rango [inicio, fin]
 *  - "single_date": selector de fecha única
 */
const DateRangeFilter = ({ config = {}, onChange, value: externalValue }) => {
  const {
    verbose_name,
    widget_type = "date_range",
    className = "",
  } = config;

  const today = useMemo(() => new Date(), []);
  const monthAgo = useMemo(() => {
    const d = new Date(today);
    d.setMonth(d.getMonth() - 1);
    return d;
  }, [today]);

  const isSingle = widget_type === "single_date";
  const defaultValue = useMemo(() => isSingle ? today : [monthAgo, today], [isSingle, today, monthAgo]);
  const [value, setValue] = useState(externalValue || defaultValue);


  const repositionOpenCalendar = useCallback((anchorEl) => {
    if (!anchorEl) return;
    const cal = document.querySelector(".flatpickr-calendar.open");
    if (!cal) return;

    const anchorRect = anchorEl.getBoundingClientRect();
    const calRect = cal.getBoundingClientRect();

    const gutter = 8;
    const viewportW = window.innerWidth;

    let left = anchorRect.left + window.scrollX;
    const maxLeft = window.scrollX + viewportW - calRect.width - gutter;
    if (left > maxLeft) left = maxLeft;
    if (left < window.scrollX + gutter) left = window.scrollX + gutter;

    const top = anchorRect.bottom + window.scrollY;

    cal.style.left = `${left}px`;
    cal.style.right = "auto";
    cal.style.top = `${top}px`;
  }, []);

  const handleInputFocus = useCallback(
    (which) => (e) => {
      if (isSingle) return;
      if (which !== "start" && which !== "end") return;
      repositionOpenCalendar(e?.target);
    },
    [isSingle, repositionOpenCalendar]
  );

  const renderInputLabel = (text) => (
    <span className="cds--data-table-header__title">{text}</span>
  );

  const handleChange = (sel) => {
    if (isSingle) {
      const date = sel instanceof Date ? sel : sel?.[0];
      if (!date) return;
      setValue(date);
      onChange?.(date);
      return;
    }

    if (!Array.isArray(sel)) return;
    const [start, endRaw] = sel;
    if (!start || !endRaw) return;

    let startAdj = start > today ? today : start;
    const endAdj = endRaw > today ? today : endRaw;
    if (startAdj > endAdj) startAdj = endAdj;

    const next = [startAdj, endAdj];
    setValue(next);
    onChange?.(next);
  };

  const inputs = useMemo(() => {
    if (isSingle) {
      return [
        <DatePickerInput
          key="single"
          id="single-date"
          labelText={renderInputLabel("Fecha")}
          placeholder="dd/mm/yyyy"
          size="md"  
        />,
      ];
    }
    return [
      <DatePickerInput
        key="start"
        id="date-range-start"
        labelText={renderInputLabel("Fecha de inicio")}
        placeholder="dd/mm/yyyy"
        size="md"  
        onFocus={handleInputFocus("start")}
        onClick={handleInputFocus("start")}
      />,
      <DatePickerInput
        key="end"
        id="date-range-end"
        labelText={renderInputLabel("Fecha de fin")}
        placeholder="dd/mm/yyyy"
        size="md"  
        onFocus={handleInputFocus("end")}
        onClick={handleInputFocus("end")}
      />,
    ];
  }, [handleInputFocus, isSingle]);

  return (
    <div
      className={[
        "date-range-filter",
        isSingle ? "date-range-filter--single" : "date-range-filter--range",
        className,
      ].join(" ")}
    >
      <DatePicker
        datePickerType={isSingle ? "single" : "range"}
        value={value}
        onChange={handleChange}
        locale="es"
        dateFormat="d/m/Y"
      >
        {inputs}
      </DatePicker>

      {!!verbose_name && (
        <p className="date-range-filter__description">{verbose_name}</p>
      )}
    </div>
  );
};

export default DateRangeFilter;
